import { useCallback, useEffect, useRef, useState } from 'react';

import type { ChatHistorySession } from './ChatHistorySession';

export type ChatHistoryStatus = 'loading' | 'loaded' | 'error';

export interface ChatHistoryQuery {
  /**
   * When set, restricts the list to sessions for this routine. When
   * null, lists all sessions across all routines (the non-routine /
   * global context).
   */
  routineID: string | null;
}

export interface ChatHistorySubscriptionHandlers {
  /** Fires with the full, sorted list on every change. */
  onSessions: (sessions: ChatHistorySession[]) => void;
  /** Fires if the subscription itself fails. */
  onError: (error: Error) => void;
}

export interface ChatHistoryOperations {
  /**
   * Opens a live subscription to the sessions matching the query and
   * returns its teardown. Implementations wrap
   * `onGetAgentSessions` (feathers-reactive `watch().find()`), which
   * emits the current rows immediately and then re-emits the whole
   * list on every create / rename / soft-delete.
   *
   * A subscription rather than a one-shot fetch is what makes a newly
   * created chat show up without a page reload — including chats
   * started in another tab or by the agent itself.
   */
  subscribe: (
    query: ChatHistoryQuery,
    handlers: ChatHistorySubscriptionHandlers,
  ) => () => void;
  /**
   * Updates the title (`patch`). Returns the updated row so the hook
   * can reflect the rename without a refetch.
   */
  rename: (sessionID: string, newTitle: string) => Promise<ChatHistorySession>;
  /**
   * Soft-deletes the session by patching `deleted: true` — a real
   * feathers `remove` cascades `agent_messages` away. The hook treats
   * 404 as idempotent success, matching `useResolveConfirmation`.
   */
  remove: (sessionID: string) => Promise<void>;
}

export interface UseChatHistoryOptions extends ChatHistoryQuery {
  /** Injected feathers operations; the production set is wired in
   * by the chat mount. */
  operations: ChatHistoryOperations;
  /** Fires on any failed operation. */
  onError?: (error: Error) => void;
}

export interface UseChatHistoryResult {
  status: ChatHistoryStatus;
  sessions: ChatHistorySession[];
  error: Error | null;
  /** Tears down and reopens the subscription — recovery path after a
   * subscription error. */
  reload: () => void;
  /** Patches the title locally + server-side. */
  renameSession: (sessionID: string, newTitle: string) => Promise<void>;
  /**
   * Removes the session locally + server-side. Resolves `true` when the
   * row is gone (404 included) and `false` when the delete failed and
   * the row was rolled back.
   */
  deleteSession: (sessionID: string) => Promise<boolean>;
}

/**
 * Drives the chat-history list off a live subscription, so sessions
 * appear and reorder as they are created and renamed rather than only
 * on a page reload. Resubscribes whenever `routineID` changes (a
 * routine-page → non-routine-page navigation flips the filter).
 *
 * Rename / delete still mutate the local list optimistically so the UI
 * stays responsive while the patch / remove is in flight; server
 * failures roll the optimistic change back and surface via `onError`.
 * The subscription is the source of truth and will overwrite either
 * outcome with the server's own view on its next emission.
 *
 * All operations are injected (`operations.subscribe / .rename /
 * .remove`) so tests can stub them and the production mount plugs in
 * the real feathers client. Callback identities are absorbed via refs
 * so callers can pass inline closures without resubscribe loops.
 */

/**
 * 404-shaped error — feathers errors carry `{ code: 404 }`; bare
 * fetch / app code might carry `status: 404` or a 404 token in the
 * message. The hook treats all three as idempotent "already gone."
 */
function isNotFoundError(error: Error): boolean {
  const candidate = error as Error & { code?: unknown; status?: unknown };

  if (candidate.code === 404 || candidate.status === 404) return true;

  return /\b404\b/.test(error.message);
}

export function useChatHistory(
  options: UseChatHistoryOptions,
): UseChatHistoryResult {
  const { routineID, operations, onError } = options;

  const operationsRef = useRef(operations);
  const onErrorRef = useRef(onError);

  operationsRef.current = operations;
  onErrorRef.current = onError;

  const [status, setStatus] = useState<ChatHistoryStatus>('loading');
  const [sessions, setSessions] = useState<ChatHistorySession[]>([]);
  const [error, setError] = useState<Error | null>(null);

  // Mirror sessions so optimistic-update callbacks can read the
  // current list synchronously when they fire (the React updater
  // for setSessions doesn't run until the next render, which is
  // too late if `await someOp()` rejects before that commit).
  const sessionsRef = useRef(sessions);

  sessionsRef.current = sessions;

  // Filter the current `sessions` belong to, so the effect can tell a
  // filter change (rows are stale) from a `reload()` (rows are fine).
  const lastRoutineIDRef = useRef(routineID);

  const [reloadCount, setReloadCount] = useState(0);

  const reload = useCallback(() => {
    setReloadCount((n) => n + 1);
  }, []);

  useEffect(() => {
    setStatus('loading');
    setError(null);

    // Drop the previous filter's rows. Only on a `routineID` change:
    // `reload()` re-runs this effect for the *same* filter, where the
    // rows on screen are still the right ones and clearing them would
    // blank the list for no reason.
    if (routineID !== lastRoutineIDRef.current) {
      lastRoutineIDRef.current = routineID;
      setSessions([]);
    }

    // Guards against a transport that delivers a queued emission
    // during or after teardown — unsubscribing should stop delivery,
    // but a late frame from the old routineID's subscription would
    // otherwise clobber the new filter's list.
    let disposed = false;

    const unsubscribe = operationsRef.current.subscribe(
      { routineID },
      {
        onSessions: (rows) => {
          if (disposed) return;

          setSessions(rows);
          // Clear a prior error: the retry operators in the feathers
          // client resubscribe after a transient auth/timeout failure,
          // so an emission after an error means we are live again.
          setError(null);
          setStatus('loaded');
        },
        onError: (err) => {
          if (disposed) return;

          setError(err);
          setStatus('error');
          onErrorRef.current?.(err);
        },
      },
    );

    return () => {
      disposed = true;
      unsubscribe();
    };
  }, [routineID, reloadCount]);

  const renameSession = useCallback(
    async (sessionID: string, newTitle: string) => {
      // Per-item revert: snapshot the row's title synchronously
      // from sessionsRef (the React updater for the optimistic
      // setSessions below doesn't run until the next commit, which
      // can be after the awaited op rejects). On failure, only the
      // affected row is touched — concurrent mutations on other
      // rows survive intact.
      const existing = sessionsRef.current.find((s) => s.id === sessionID);

      if (!existing) return;

      const originalTitle = existing.title;

      setSessions((current) =>
        current.map((s) =>
          s.id === sessionID ? { ...s, title: newTitle } : s,
        ),
      );

      try {
        const updated = await operationsRef.current.rename(sessionID, newTitle);

        setSessions((current) =>
          current.map((s) => (s.id === sessionID ? updated : s)),
        );
      } catch (err) {
        setSessions((current) =>
          current.map((s) =>
            s.id === sessionID ? { ...s, title: originalTitle } : s,
          ),
        );

        const wrapped = err instanceof Error ? err : new Error(String(err));

        onErrorRef.current?.(wrapped);
      }
    },
    [],
  );

  const deleteSession = useCallback(async (sessionID: string) => {
    // Per-item revert: snapshot the row + its index from
    // sessionsRef, optimistically remove, and on failure re-insert
    // just that row near its original position. Other rows in
    // flight remain untouched.
    const existingIndex = sessionsRef.current.findIndex(
      (s) => s.id === sessionID,
    );

    // Already absent locally — nothing to remove.
    if (existingIndex < 0) return true;

    const existing = sessionsRef.current[existingIndex];

    setSessions((current) => current.filter((s) => s.id !== sessionID));

    try {
      await operationsRef.current.remove(sessionID);

      return true;
    } catch (err) {
      const wrapped = err instanceof Error ? err : new Error(String(err));

      // 404 is idempotent success — the row is gone server-side, so
      // leave the local list with it removed and don't surface as an
      // error.
      if (isNotFoundError(wrapped)) {
        return true;
      }

      setSessions((current) => {
        if (current.some((s) => s.id === sessionID)) return current;

        const next = current.slice();
        const insertAt = Math.min(existingIndex, next.length);

        next.splice(insertAt, 0, existing);

        return next;
      });

      onErrorRef.current?.(wrapped);

      return false;
    }
  }, []);

  return {
    status,
    sessions,
    error,
    reload,
    renameSession,
    deleteSession,
  };
}
